import { Hotel, ShieldCheck, Soup, Users, UtensilsCrossed, Wallet, type LayoutDashboard } from 'lucide-react';
import type { AppModule } from '@overseer/contracts';
import { HR_NAV } from '@/features/hr/nav';
import { HOTEL_NAV } from '@/features/hotel/nav';
import { GASTRONOMY_NAV } from '@/features/gastronomy/nav';
import { CATERING_NAV } from '@/features/catering/nav';
import { CASHIER_NAV } from '@/features/cashier/nav';
import { ADMIN_NAV } from '@/features/admin/nav';
import type { ModuleNav } from '@/components/layout/module-nav';

/**
 * What the client knows about each application.
 *
 * One entry per module, and **this is the only file that has to change when a
 * third arrives** — the picker, the switcher and the shell all read from here
 * rather than each carrying their own list. The server decides *which* modules a
 * user may open (`modules` on `/v1/me`); this decides what they look like.
 *
 * Keyed by the contract's `AppModule`, so the compiler requires an entry for
 * every module the server can name. A module added to the enum with no entry
 * here fails to build rather than rendering a blank tile.
 */
export type ModuleDefinition = {
  /** Where the switcher and the picker send you. The module's root route. */
  to: string;
  /** i18n key for the name. */
  labelKey: string;
  /** i18n key for the one-line description on the picker. */
  descriptionKey: string;
  icon: typeof LayoutDashboard;
  nav: ModuleNav;
};

export const MODULES: Record<AppModule, ModuleDefinition> = {
  hr: {
    to: '/hr',
    labelKey: 'modules.hr.name',
    descriptionKey: 'modules.hr.description',
    icon: Users,
    nav: HR_NAV,
  },
  hotel: {
    to: '/hotel',
    labelKey: 'modules.hotel.name',
    descriptionKey: 'modules.hotel.description',
    icon: Hotel,
    nav: HOTEL_NAV,
  },
  gastronomy: {
    to: '/gastronomy',
    labelKey: 'modules.gastronomy.name',
    descriptionKey: 'modules.gastronomy.description',
    icon: UtensilsCrossed,
    nav: GASTRONOMY_NAV,
  },
  catering: {
    to: '/catering',
    labelKey: 'modules.catering.name',
    descriptionKey: 'modules.catering.description',
    icon: Soup,
    nav: CATERING_NAV,
  },
  cashier: {
    to: '/cashier',
    labelKey: 'modules.cashier.name',
    descriptionKey: 'modules.cashier.description',
    icon: Wallet,
    nav: CASHIER_NAV,
  },
  admin: {
    to: '/admin',
    labelKey: 'modules.admin.name',
    descriptionKey: 'modules.admin.description',
    icon: ShieldCheck,
    nav: ADMIN_NAV,
  },
};

/**
 * Which module a path belongs to, or null.
 *
 * Used by the shell to pick a nav bar from the URL rather than from state, so a
 * deep link renders the right chrome on the first paint — no flash of the wrong
 * module's nav while something works it out.
 */
export const moduleForPath = (pathname: string): AppModule | null => {
  const entries = Object.entries(MODULES) as [AppModule, ModuleDefinition][];

  const found = entries.find(
    ([, definition]) => pathname === definition.to || pathname.startsWith(`${definition.to}/`),
  );

  return found ? found[0] : null;
};
